import React, { useEffect, useRef, useState } from 'react';

const CLOUDVIZ_BASE_URL = process.env.REACT_APP_CLOUDVIZ_BASE_URL || '/cloudviz';
const LOADER_SCRIPT_ID = 'cloudviz-unity-loader-script';

function sendAvatarUpdate(unityInstance, params) {
  if (!unityInstance || !params?.data) {
    return;
  }

  unityInstance.SendMessage('TSFramePlayer', 'SetTSFrame', params.data);
  unityInstance.SendMessage('TSFramePlayer', 'SetUserID', params.userId || 'Unknown');
  unityInstance.SendMessage('TSFramePlayer', 'SetDateTime_String', params.dateInString || new Date().toString());
}

async function fetchTSFrameText(path) {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to fetch TSFrame file (${response.status})`);
  }
  return response.text();
}

function loadUnityLoaderScript(loaderUrl) {
  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(LOADER_SCRIPT_ID);
    if (existingScript) {
      if (window.createUnityInstance) {
        resolve();
        return;
      }

      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load CloudViz loader script')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = LOADER_SCRIPT_ID;
    script.src = loaderUrl;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load CloudViz loader script'));
    document.body.appendChild(script);
  });
}

const CloudVizPlayer = ({ tsframePath, userId, dateInString }) => {
  const canvasRef = useRef(null);
  const unityInstanceRef = useRef(null);

  const [isUnityReady, setIsUnityReady] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [status, setStatus] = useState('Initializing CloudViz...');
  const [error, setError] = useState('');

  useEffect(() => {
    let isCancelled = false;

    const setupUnity = async () => {
      if (unityInstanceRef.current || !canvasRef.current) {
        return;
      }

      try {
        const buildUrl = `${CLOUDVIZ_BASE_URL}/Build`;
        const loaderUrl = `${buildUrl}/CloudViz.loader.js`;

        await loadUnityLoaderScript(loaderUrl);

        if (typeof window.createUnityInstance !== 'function') {
          throw new Error('createUnityInstance is not available after script load');
        }

        const config = {
          dataUrl: `${buildUrl}/CloudViz.data`,
          frameworkUrl: `${buildUrl}/CloudViz.framework.js`,
          codeUrl: `${buildUrl}/CloudViz.wasm`,
          streamingAssetsUrl: `${CLOUDVIZ_BASE_URL}/StreamingAssets`,
          companyName: 'TuringSense',
          productName: 'PIVOT PT',
          productVersion: '0.12'
        };

        const unityInstance = await window.createUnityInstance(
          canvasRef.current,
          config,
          (progress) => {
            if (!isCancelled) {
              setLoadProgress(progress);
              setStatus(`Loading CloudViz... ${Math.round(progress * 100)}%`);
            }
          }
        );

        if (isCancelled) {
          if (unityInstance?.Quit) {
            await unityInstance.Quit();
          }
          return;
        }

        unityInstanceRef.current = unityInstance;
        setIsUnityReady(true);
        setStatus('CloudViz ready');
      } catch (initError) {
        if (!isCancelled) {
          setError(initError.message || 'CloudViz initialization failed');
          setStatus('CloudViz failed to load');
        }
      }
    };

    setupUnity();

    return () => {
      isCancelled = true;

      if (unityInstanceRef.current?.Quit) {
        unityInstanceRef.current.Quit().catch(() => {
          // Ignore shutdown errors to avoid blocking unmount.
        });
      }

      unityInstanceRef.current = null;
      setIsUnityReady(false);
    };
  }, []);

  useEffect(() => {
    let isStale = false;

    const pushTSFrameData = async () => {
      const unityInstance = unityInstanceRef.current;
      if (!isUnityReady || !unityInstance) {
        return;
      }

      if (!tsframePath) {
        setStatus('Select a phase with animation path');
        return;
      }

      try {
        setError('');
        setStatus('Fetching TSFrame data...');

        const textData = await fetchTSFrameText(tsframePath);
        if (isStale) {
          return;
        }

        sendAvatarUpdate(unityInstance, {
          data: textData,
          userId,
          dateInString
        });

        setStatus('TSFrame loaded into CloudViz');
      } catch (loadError) {
        if (!isStale) {
          setError(loadError.message || 'Failed to load TSFrame');
          setStatus('TSFrame load failed');
        }
      }
    };

    pushTSFrameData();

    return () => {
      isStale = true;
    };
  }, [isUnityReady, tsframePath, userId, dateInString]);

  return (
    <div className="mx-auto max-w-[760px] rounded-lg border border-blue-200/60 bg-white p-3 shadow-sm dark:border-blue-700/30 dark:bg-gray-900/40">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h4 className="text-md font-semibold text-blue-900 dark:text-blue-100">CloudViz Preview</h4>
          <p className="text-xs text-blue-700 dark:text-blue-300">Source: {tsframePath || 'No animation path selected'}</p>
        </div>
        <button
          type="button"
          className="rounded border border-blue-400 px-2 py-1 text-xs text-blue-700 hover:bg-blue-50 dark:border-blue-500 dark:text-blue-300 dark:hover:bg-blue-900/30"
          onClick={() => unityInstanceRef.current?.SetFullscreen?.(1)}
          disabled={!isUnityReady}
        >
          Fullscreen
        </button>
      </div>

      <div className="relative overflow-hidden rounded border border-gray-300 bg-gray-900 dark:border-gray-700">
        <canvas
          id="unity-canvas"
          ref={canvasRef}
          width={720}
          height={450}
          tabIndex={-1}
          className="h-auto w-full"
        />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-gray-600 dark:text-gray-300">{status}</span>
        {!isUnityReady && !error && <span className="text-blue-600 dark:text-blue-300">{Math.round(loadProgress * 100)}%</span>}
      </div>

      {error && (
        <div className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300">
          {error}
        </div>
      )}
    </div>
  );
};

export default CloudVizPlayer;
